import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { getArticleDateTime, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import Link from 'next/link'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { AirconQuickLinks } from '../../../components/index/service-category/aircon-service/AirconQuickLinks'
import { useRouter } from 'next/router'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'
import { AirconRepair } from '@/index/service-category/aircon-service/AirconRepair'
import { AirconServiceMatcher } from '@/index/service-category/aircon-service/AirconServiceMatcher'
import { HowToBookAirconService } from '@/index/service-category/aircon-service/HowToBookAirconService'
import { AirconWhyUs } from '@/index/service-category/aircon-service/AirconWhyUs'
import { AirconCustomerReviews } from '@/index/service-category/aircon-service/AirconCustomerReviews'
import { AirconBrandsWeSupport } from '@/index/service-category/aircon-service/AirconBrandsWeSupport'
import { Faq } from '../../../components/commons/Faq'
import { acUrgentFaqs } from '../../../utils/faq'
import { getTestimonials } from 'api/user/profile-api'
import { getAcTestimonials, setAcTestimonials } from 'service/testimonial-store'
import { useState } from 'react'
import { RequestProvider } from 'context/RequestContext'
import { Services } from '@/index/service/Services'
import { whatsAppDisplay, whatsAppUrl } from 'utils/business-info'
import { localBusinessNode, buildServiceNode } from 'utils/local-business-schema'

// Hero doubles as the og:image, so both point at the same asset - a share card that
// does not match the page it opens is a trust signal we were throwing away.
const heroImage = "blog/living/repairman-doing-air-conditioner-service.jpg"
const pageUrl = "https://www.stringssg.com/sg/aircon-servicing-singapore/urgent-aircon-servicing"
const pageTitle = "Urgent Aircon Servicing Singapore | 24/7 Booking, Same-Day Repair"
// One description, reused across meta/og/twitter so they can never drift apart.
const pageDescription = "Urgent aircon servicing in Singapore with same-day slots. Submit a request any hour, technicians attend 8am to 10pm daily. From $47.84 per unit, transport included, no callout fee."

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    const { month, year } = getArticleDateTime();
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const testimonials = getAcTestimonials().length > 0 ? getAcTestimonials() : await getTestimonials("Aircon Services", 0, 6)
    setAcTestimonials(testimonials)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        wpNewPosts,
        wpPosts,
        testimonials,
        countryName
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const testimonials = props.testimonials
    const countryName = props.countryName

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

    // Same figures as the hasOfferCatalog on the hub page and the Service node below -
    // one set of prices across the site, or the rich results contradict the page.
    const urgentPricing = [
        { service: "General servicing - 1 unit", price: "$47.84", note: "Same-day slot subject to availability" },
        { service: "General servicing - 2 units", price: "$57.41", note: "Works out to about $28.70 per unit" },
        { service: "Chemical wash - per unit", price: "$82.80", note: "For weak airflow or a musty smell" },
        { service: "Chemical overhaul - per unit", price: "$149.04", note: "Fan coil comes off the wall for a full clean" },
        { service: "Refrigerant gas top-up - per unit", price: "$147.20", note: "Full tank, after the leak is located" },
    ]

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={pageDescription}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content={pageUrl} />
        <meta property="og:type"   content="website" />
        <meta property="og:title"    content={pageTitle}/>
        <meta property="og:description" content={pageDescription} />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content={s3Static + heroImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={s3Static + heroImage} />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={pageUrl}/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content={pageDescription}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={pageUrl}/>
        {/* Service node references the hub's LocalBusiness by @id rather than redeclaring
            NAP, and its hoursAvailable matches that LocalBusiness's openingHours (08:00-22:00).
            The platform accepts requests around the clock; technicians attend within those hours. */}
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@type":"WebPage","@id":"${pageUrl}/#webpage","url":"${pageUrl}","name":"${pageTitle}","description":"${pageDescription}","inLanguage":"en-SG","primaryImageOfPage":{"@type":"ImageObject","url":"${s3Static + heroImage}"},"datePublished":"2025-01-25T07:30:46+00:00","dateModified":"${modifiedDate}","isPartOf":{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG"}},{"@type":"BreadcrumbList","@id":"${pageUrl}/#breadcrumblist","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"${pageUrl}","name":"Urgent Aircon Servicing","url":"${pageUrl}"}}]},{"@type":"Service","@id":"${pageUrl}/#service","name":"Urgent Aircon Servicing Singapore","serviceType":"Emergency Aircon Repair","description":"Urgent and same-day aircon servicing and repair in Singapore for water leaks, units that will not power on, tripping breakers and loss of cooling. Requests accepted 24 hours, technicians attend 8am to 10pm daily.","url":"${pageUrl}","image":"${s3Static + heroImage}","provider":${JSON.stringify(localBusinessNode)},"areaServed":{"@type":"Country","name":"Singapore"},"availableChannel":{"@type":"ServiceChannel","serviceUrl":"${pageUrl}","availableLanguage":["en"],"servicePhone":{"@type":"ContactPoint","contactType":"customer service","telephone":"+65 9351 2695","url":"https://wa.me/6593512695"}},"hoursAvailable":{"@type":"OpeningHoursSpecification","dayOfWeek":["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],"opens":"08:00","closes":"22:00"},"hasOfferCatalog":{"@type":"OfferCatalog","name":"Urgent Aircon Services and Prices in Singapore","itemListElement":[{"@type":"Offer","name":"General Aircon Servicing - 1 unit","priceCurrency":"SGD","price":"47.84","priceSpecification":{"@type":"PriceSpecification","priceCurrency":"SGD","price":"47.84","valueAddedTaxIncluded":true},"itemOffered":{"@type":"Service","name":"General Aircon Servicing"}},{"@type":"Offer","name":"General Aircon Servicing - 2 units","priceCurrency":"SGD","price":"57.41","itemOffered":{"@type":"Service","name":"General Aircon Servicing"}},{"@type":"Offer","name":"Aircon Chemical Wash - 1 unit","priceCurrency":"SGD","price":"82.80","itemOffered":{"@type":"Service","name":"Aircon Chemical Wash"}},{"@type":"Offer","name":"Aircon Chemical Overhaul - 1 unit","priceCurrency":"SGD","price":"149.04","itemOffered":{"@type":"Service","name":"Aircon Chemical Overhaul"}},{"@type":"Offer","name":"Refrigerant Gas Top Up - 1 unit full tank","priceCurrency":"SGD","price":"147.20","itemOffered":{"@type":"Service","name":"Aircon Gas Top Up"}}]}}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="urgent-aircon-servicing" className='relative'>
                    <AirconRepair
                        setRequestForm={setRequestForm}
                        setIsQuickBook={setIsQuickBook}
                        title={"Urgent Aircon Servicing Singapore"}
                        subtitle="Same-day slots, technicians 8am to 10pm daily. Transport included, no callout fee."
                        problem={"Need emergency AC service"}
                        heroImage={heroImage}
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm &&
                    <Services
                        requestType="Aircon Services"
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>

            <div className='max-w-5xl mx-auto py-6 md:py-10 px-4'>
                <div className='space-y-4 leading-relaxed'>
                    <p>
                        <strong>Urgent aircon servicing in Singapore</strong> is for the faults that cannot wait until the weekend &mdash; water
                        running down the wall, an outdoor unit tripping your breaker, or a bedroom unit that has stopped
                        cooling on a 33&deg;C night. Submit the request at any hour and we will assign a technician in your
                        area, with same-day slots available during our service window.
                    </p>
                    <p>
                        Our booking platform takes urgent aircon requests <strong>24 hours a day</strong> and technicians attend
                        between <strong>8am and 10pm, seven days a week</strong>, including public holidays. Pricing is the same as a
                        scheduled booking &mdash; there is no emergency surcharge and no callout fee, and transport is already
                        included in every price you see.
                    </p>
                </div>
            </div>

            {/* 11 internal links to the sibling fault pages, phrased as symptoms - which is
                how someone with an aircon emergency actually searches. */}
            <AirconServiceMatcher/>

            <div className='max-w-5xl mx-auto py-6 md:py-10 px-4 space-y-5'>
                <h2 className='text-2xl md:text-3xl font-bold text-gray-900'>
                    How Fast Can We Get There?
                </h2>
                <div className='grid md:grid-cols-2 gap-6 items-center'>
                    <div className='space-y-4 leading-relaxed'>
                        <p>
                            Urgent requests submitted inside the <strong>8am to 10pm</strong> window are assigned the same day
                            wherever a technician is free nearby. Anything submitted overnight sits at the front of the
                            queue and is assigned when the window opens the next morning.
                        </p>
                        <p>
                            The fastest route is WhatsApp &mdash; send a photo or a short video of the fault and the technician
                            can bring the right parts on the first visit instead of returning a second time.
                        </p>
                        <p className='font-semibold'>
                            Before the technician arrives: switch the unit off at the wall, put a bucket and towels under
                            any active leak, and clear furniture from underneath the fan coil unit.
                        </p>
                        <a
                            href={`${whatsAppUrl}?text=${encodeURIComponent("Hi, I need urgent aircon servicing.")}`}
                            target='_blank'
                            rel='noreferrer'
                            className='inline-block text-base py-3 px-4 text-orange-600 border border-amber-600 rounded-lg'
                        >
                            WhatsApp {whatsAppDisplay}
                        </a>
                    </div>
                    <div className='relative w-full h-56 md:h-96 rounded-xl'>
                        <Image className='rounded-xl opacity-90'
                            src={s3Static + "blog/living/singapore-aircon-technician.png"}
                            fill
                            sizes="(max-width: 768px) 100vw, 50vw"
                            style={{objectFit: 'contain', objectPosition: 'center'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='Technician arriving for urgent aircon servicing in Singapore - StringsSG'
                        />
                    </div>
                </div>
            </div>

            <HowToBookAirconService/>

            <div className='max-w-5xl mx-auto py-6 md:py-10 px-4 space-y-5'>
                <h2 className='text-2xl md:text-3xl font-bold text-gray-900'>
                    Urgent Aircon Repair Cost in Singapore
                </h2>
                <p className='leading-relaxed'>
                    An urgent booking costs exactly the same as a scheduled one. Every price below is the final
                    all-in amount with transport included, shown to you before you confirm, with nothing added
                    after the job is done.
                </p>
                <div className='overflow-hidden rounded-xl border border-gray-100 shadow-sm'>
                    <table className='w-full text-sm text-left'>
                        <caption className='sr-only'>
                            Urgent aircon servicing and repair prices in Singapore, transport included
                        </caption>
                        <thead className='bg-gray-50 text-gray-700'>
                            <tr>
                                <th scope='col' className='px-4 py-3 font-semibold'>Service</th>
                                <th scope='col' className='px-4 py-3 font-semibold'>From</th>
                                <th scope='col' className='hidden md:table-cell px-4 py-3 font-semibold'>Notes</th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-100'>
                            {urgentPricing.map((row) => (
                                <tr key={row.service}>
                                    <th scope='row' className='px-4 py-3 font-medium text-gray-900 align-top'>
                                        {row.service}
                                        <span className='block md:hidden text-gray-500 font-normal mt-1'>{row.note}</span>
                                    </th>
                                    <td className='px-4 py-3 align-top font-semibold text-orange-600 whitespace-nowrap'>{row.price}</td>
                                    <td className='hidden md:table-cell px-4 py-3 text-gray-600 align-top'>{row.note}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className='leading-relaxed'>
                    Parts replacement &mdash; a PCB, capacitor, fan motor or compressor &mdash; is quoted after the technician
                    has diagnosed the fault, and never carried out without your approval. See the full{' '}
                    <Link href="/sg/aircon-servicing-singapore/aircon-servicing-price-singapore" prefetch={false} className='text-orange-600'>
                        aircon servicing price list
                    </Link>{' '}or book a{' '}
                    <Link href="/sg/aircon-servicing-singapore/aircon-repair" prefetch={false} className='text-orange-600'>
                        diagnostic repair visit
                    </Link>.
                </p>
                <div className='text-center pt-2'>
                    <Link href="/sg/aircon-servicing-singapore" prefetch={false} className='text-base py-3 px-4 text-orange-600 border border-amber-600 rounded-lg'>
                        Book Urgent Aircon Service
                    </Link>
                </div>
            </div>

            <AirconWhyUs/>
            <AirconCustomerReviews testimonials={testimonials} />
            <AirconBrandsWeSupport/>

            <section className='max-w-5xl mx-auto py-6 md:py-10 px-2' aria-label="Frequently Asked Questions">
                <Faq
                    questionsAnswers={acUrgentFaqs}
                    title="Urgent Aircon Servicing FAQs"
                />
            </section>

            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
            <AirconQuickLinks/>
        </div>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
